import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Lock, Eye, EyeOff, User, CheckCircle, AlertCircle } from 'lucide-react';
import { validateInvitationToken, clearTokenData } from '../../store/slices/invitationSlice';
import { loginUser } from '../../store/slices/authSlice';
import { getApi } from '../../utils/api';
import CollegeBadge from '../../components/common/CollegeBadge';
import toast from 'react-hot-toast';

const RegisterWithToken = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const { tokenData, isLoading, error } = useSelector((state) => state.invitations);
    const { isAuthenticated } = useSelector((state) => state.auth);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Rediriger si déjà connecté
    useEffect(() => {
        if (isAuthenticated) navigate('/dashboard', { replace: true });
    }, [isAuthenticated, navigate]);

    // Valider le token au chargement
    useEffect(() => {
        if (!token) return;
        dispatch(validateInvitationToken(token));
        return () => dispatch(clearTokenData());
    }, [token, dispatch]);

    // Pré-remplir le username à partir du prénom/nom
    useEffect(() => {
        if (tokenData && !username) {
            const suggested = `${tokenData.firstName}.${tokenData.lastName}`
                .toLowerCase()
                .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9_-]/g, '')
                .slice(0, 50);
            setUsername(suggested);
        }
    }, [tokenData]);

    const validate = () => {
        const errors = {};
        if (!username || username.length < 3) errors.username = 'Au moins 3 caractères';
        if (!/^[a-zA-Z0-9_-]+$/.test(username)) errors.username = 'Lettres, chiffres, _ et - uniquement';
        if (!password || password.length < 8) errors.password = 'Au moins 8 caractères';
        if (password !== confirmPassword) errors.confirmPassword = 'Les mots de passe ne correspondent pas';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const api = await getApi();
            const response = await api.post('/auth/register-with-token', { token, username, password });
            const payload = response.data || response;
            if (payload?.data?.token || payload?.token) {
                const jwtToken = payload.data?.token || payload.token;
                localStorage.setItem('token', jwtToken);
                toast.success('Compte créé avec succès ! Bienvenue 🎉');
                dispatch({ type: 'auth/login/fulfilled', payload: payload.data || payload });
                navigate('/dashboard', { replace: true });
            }
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Erreur lors de la création du compte';
            toast.error(msg);
            if (msg.includes('utilisateur')) setFormErrors(prev => ({ ...prev, username: 'Ce nom est déjà pris' }));
        } finally {
            setSubmitting(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center max-w-md px-6">
                    <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-800 mb-2 font-ginka">Lien invalide</h2>
                    <p className="text-gray-500 font-ginka">Ce lien d'invitation est incomplet ou incorrect.</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="w-8 h-8 border-4 border-primary-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-ginka">Vérification de votre invitation...</p>
                </div>
            </div>
        );
    }

    if (error && !tokenData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full text-center">
                    <AlertCircle className="w-16 h-16 text-primary-red mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-800 mb-2 font-ginka h3-charte">Invitation invalide</h2>
                    <p className="text-gray-600 mb-6 font-ginka p-charte">{error}</p>
                    <p className="text-sm text-gray-400">
                        Si vous pensez que c'est une erreur, contactez un administrateur.
                    </p>
                </div>
            </div>
        );
    }

    if (!tokenData) return null;

    const expiresFormatted = new Date(tokenData.expiresAt).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric'
    });

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
            <div className="max-w-md w-full space-y-6">
                {/* Header */}
                <div className="text-center">
                    <img src="/logo.png" alt="Logo" className="h-16 w-auto object-contain mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 font-ginka h2-charte">Créer votre compte</h1>
                    <p className="text-gray-500 text-sm mt-1 font-ginka p-charte">Plateforme de formation Plateau Urbain</p>
                </div>

                {/* Bandeau invitation */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-green-800 font-medium text-sm font-ginka">Invitation valide</p>
                        <p className="text-green-600 text-xs mt-0.5 font-ginka">Expire le {expiresFormatted}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-5">
                    {/* Collège */}
                    <div className="flex items-center space-x-3 pb-4 border-b border-gray-100">
                        <p className="text-sm text-gray-500 font-ginka">Votre collège :</p>
                        <CollegeBadge college={tokenData.college} size="md" />
                    </div>

                    {/* Champs verrouillés */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">Prénom</label>
                            <input value={tokenData.firstName} disabled
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm font-ginka cursor-not-allowed" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">Nom</label>
                            <input value={tokenData.lastName} disabled
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm font-ginka cursor-not-allowed" />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">Email</label>
                        <input value={tokenData.email} disabled
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm font-ginka cursor-not-allowed" />
                    </div>

                    {tokenData.organization && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">Structure</label>
                            <input value={tokenData.organization} disabled
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm font-ginka cursor-not-allowed" />
                        </div>
                    )}

                    <div className="border-t border-gray-100 pt-4 space-y-4">
                        {/* Username */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">
                                Nom d'utilisateur *
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className={`w-full pl-9 pr-3 py-2 border rounded-lg text-sm font-ginka focus:outline-none focus:ring-2 focus:ring-primary-red ${formErrors.username ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="votre.nom"
                                />
                            </div>
                            {formErrors.username && <p className="text-red-500 text-xs mt-1">{formErrors.username}</p>}
                            <p className="text-gray-400 text-xs mt-1">Vous pouvez modifier cette suggestion</p>
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">
                                Mot de passe * <span className="text-gray-400 font-normal">(min. 8 caractères)</span>
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className={`w-full pl-9 pr-10 py-2 border rounded-lg text-sm font-ginka focus:outline-none focus:ring-2 focus:ring-primary-red ${formErrors.password ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Votre mot de passe"
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                        </div>

                        {/* Confirm password */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">
                                Confirmer le mot de passe *
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type={showConfirm ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={`w-full pl-9 pr-10 py-2 border rounded-lg text-sm font-ginka focus:outline-none focus:ring-2 focus:ring-primary-red ${formErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'}`}
                                    placeholder="Confirmez votre mot de passe"
                                />
                                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            {formErrors.confirmPassword && <p className="text-red-500 text-xs mt-1">{formErrors.confirmPassword}</p>}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-charte btn-charte-primary w-full py-3 font-medium disabled:opacity-50"
                    >
                        {submitting ? (
                            <span className="flex items-center justify-center space-x-2">
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span>Création en cours...</span>
                            </span>
                        ) : 'Créer mon compte'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default RegisterWithToken;
